import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import App from './App.tsx';
import { logClientDiagnostic, stashClientDiagnostic } from '@/lib/client-diagnostics';
import './index.css';

/**
 * Async error capture. ErrorBoundary catches render-phase errors, but
 * unhandled promise rejections (rejected fetches in a useEffect's .then
 * chain, rejected edge-function calls that nobody awaited) bypass React
 * entirely. In local/dev-preview builds we keep verbose diagnostics in
 * sessionStorage + console; production stores only a redacted marker.
 *
 * This is the minimum viable replacement for Sentry's window handlers
 * now that Sentry has been removed from the stack.
 */
function stashError(label: string, detail: unknown) {
  const err = detail as { stack?: string; message?: string } | undefined;
  logClientDiagnostic('error', label, detail);
  stashClientDiagnostic(
    label,
    {
      message: err?.message ?? String(detail),
      stack: err?.stack,
    },
    {
      kind: label,
      message: 'An async client error was captured.',
    },
  );
}
window.addEventListener('unhandledrejection', (e) => stashError('unhandledrejection', e.reason));
window.addEventListener('error', (e) => stashError('window.error', e.error ?? e.message));

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

createRoot(root).render(
  <React.StrictMode>
    <HelmetProvider>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </HelmetProvider>
  </React.StrictMode>
);
